import { Backdrop, CircularProgress, Typography } from '@mui/material';
import { FC } from 'react';

interface LoadingBackdropProps {
  /** Whether the backdrop is open */
  open: boolean;
  /** The title text to display */
  title?: string;
  /** The subtitle/description text to display */
  subtitle?: string;
  /** Custom z-index for the backdrop */
  zIndex?: number;
  /** Custom background color opacity */
  backgroundColor?: string;
}

/**
 * A reusable loading backdrop component that shows a semi-transparent overlay
 * with a loading spinner and optional text when an operation is in progress.
 *
 * Features:
 * - Semi-transparent backdrop that blocks interactions
 * - Centered loading spinner
 * - Optional title and subtitle text
 * - Customizable z-index and background color
 * - Consistent styling across the application
 *
 * @example
 * // Basic usage
 * <LoadingBackdrop open={isLoading} />
 *
 * @example
 * // With custom text
 * <LoadingBackdrop
 *   open={isDeleting}
 *   title="Deleting..."
 *   subtitle="Please wait while the item is being deleted."
 * />
 *
 * @example
 * // With custom styling
 * <LoadingBackdrop
 *   open={isProcessing}
 *   title="Processing..."
 *   zIndex={1400}
 *   backgroundColor="rgba(0, 0, 0, 0.7)"
 * />
 *
 * @component
 * @param {LoadingBackdropProps} props - Component props
 * @returns {JSX.Element} Rendered LoadingBackdrop component
 */
export const LoadingBackdrop: FC<LoadingBackdropProps> = ({
  open,
  title,
  subtitle,
  zIndex,
  backgroundColor = 'rgba(0, 0, 0, 0.5)',
}) => {
  return (
    <Backdrop
      open={open}
      sx={{
        position: 'absolute',
        // Keep this below the MUI dialog z-index (~1300) by default so dialogs
        // are always clickable above the loading overlay. Callers can override.
        zIndex: zIndex ?? 1250,
        backgroundColor,
        color: 'white',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <CircularProgress color="inherit" />
      {title ? (
        <Typography variant="h6" align="center">
          {title}
        </Typography>
      ) : null}
      {subtitle ? (
        <Typography variant="body2" align="center" sx={{ maxWidth: 300 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Backdrop>
  );
};
